import { Box, Flex } from '@chakra-ui/react';

import { BrowserRouter as Router, Route, Routes, useLocation,Navigate, useNavigate, matchPath } from "react-router-dom";
import { useEffect, useState } from 'react'; 

import { jwtDecode } from "jwt-decode";
import { useToast } from "@chakra-ui/react";
import ChangeB2CPassword from '../Common/ChangeB2CPassword';
import NavBarB2C from '../Common/NavBarB2C';

import axios from "axios";
import B2CAdminRouter from './B2CAdminRouter';
import B2CAssessorRouter from './B2CAssessorRouter';
import B2CLogin from '../Public/B2CLogin';
import B2CLandingPage from '../Public/B2CLandingPage';
import B2CDomainPage from '../Public/B2CDomainPage';
import B2COfferPage from '../Public/B2COfferPage';
import B2CCheckoutPage from '../Public/B2CCheckoutPage';
import B2CFreeResources from '../Public/B2CFreeResources';
import B2CStudentRouter from './B2CStudentRouter';
import { StudentContextProvider } from '../Student/B2CStudentContext';
import B2CResetPassword from '../Public/B2CResetPassword';
import B2CPaymentSuccess from '../Public/B2CPaymentSuccess';
import B2CContactUsPage from '../Public/B2CContactUsPage';
import { TOKEN_UPDATED_EVENT } from '../../apiClient'; // adjust path
import B2CBlogsPage from '../Public/B2CBlogsPage';
import B2CBlogDetailsPage from '../Public/B2CBlogDetailsPage';
import B2CPrivacyPolicy from '../Public/B2CPrivacyPolicy';
import B2CTermsAndConditions from '../Public/B2CTermsAndConditions';
import B2CRefundPolicy from '../Public/B2CRefundPolicy';
import B2CCookiePolicy from '../Public/B2CCookiePolicy';
import B2CAcceptableUsePolicy from '../Public/B2CAcceptableUsePolicy';
import { StudentSidebarProvider } from '../Student/Layouts/StudentSidebarContext';


/* const useB2CTokenExpiration = (
  b2c_token: string | null,
  onLogout: () => void
) => {
  const toast = useToast();

  useEffect(() => {
    if (!b2c_token) return;

    try {
      const decoded: { exp: number } = jwtDecode(b2c_token); // Decode the b2c_token to get the expiration time
      const currentTime = Math.floor(Date.now() / 1000); // Get current time in seconds
      const timeRemaining = decoded.exp - currentTime; // Calculate time left until expiration

      if (timeRemaining > 0) {
        // Show a warning toast 10 minutes before expiration (if timeRemaining is more than 10 minutes)
        if (timeRemaining > 600) {
          const warningTimeout = setTimeout(() => {
            toast({
              title: "Session Expiring Soon",
              description:
                "Your session will expire in 10 minutes. Please save your work.",
              status: "warning",
              duration: 9000, // Toast stays for 9 seconds
              isClosable: true,
            });
          }, (timeRemaining - 600) * 1000); // Trigger 10 minutes before expiration

          // Cleanup warning timeout
          return () => clearTimeout(warningTimeout);
        }

        // Set timeout to logout when b2c_token expires
        const logoutTimeout = setTimeout(() => {
          console.log("B2c_token expired. Logging out...");
          onLogout();
        }, timeRemaining * 1000);

        // Cleanup logout timeout
        return () => clearTimeout(logoutTimeout);
      } else {
        onLogout(); // B2c_token already expired, logout immediately
      }
    } catch (err) {
      console.error("Failed to decode b2c_token:", err);
      onLogout(); // In case of error, consider the b2c_token invalid and logout
    }
  }, [b2c_token, onLogout, toast]);
};
 */


const useB2CTokenExpiration = (b2c_token: string | null) => {
  const toast = useToast();

  useEffect(() => {
    if (!b2c_token) return;

    try {
      const decoded: { exp: number } = jwtDecode(b2c_token);
      const currentTime = Math.floor(Date.now() / 1000);
      const timeRemaining = decoded.exp - currentTime;

      if (timeRemaining > 600) {
        const warningTimeout = setTimeout(() => {
          toast({
            title: "Session Refreshing",
            description: "Keeping your session active...",
            status: "info",
            duration: 4000,
            isClosable: true,
          });
        }, (timeRemaining - 600) * 1000);

        return () => clearTimeout(warningTimeout);
      }
      // No forced logout here -- the apiClient interceptor handles
      // real session expiry (i.e. when the refresh token itself is
      // invalid/expired) by redirecting to /practice/login directly.
    } catch (err) {
      console.error("Failed to decode b2c_token:", err);
    }
  }, [b2c_token, toast]);
};


interface B2c_tokenData {
  sub: string;
  role_code: string;
  is_initial_password: boolean;
}

// Utility function to decode b2c_token
const decodeB2c_token = (b2c_token: string | null): B2c_tokenData | null => {
  try {
      return b2c_token ? jwtDecode<B2c_tokenData>(b2c_token) : null;
  } catch (error) {
      console.error("Failed to decode b2c_token:", error);
      return null;
  }
};

// Only a checkout page ever sets `from`, so accept only that shape.
// Anything else falls back to the normal dashboard redirect.
const CHECKOUT_PATH_REGEX = /^\/practice\/[^/]+\/[^/]+\/checkout\/?$/;

const getSafeReturnTo = (from: unknown): string | null =>
  typeof from === "string" && CHECKOUT_PATH_REGEX.test(from) ? from : null;

const B2CRouter = () => {

    const location = useLocation();

    const navigate = useNavigate();
    const [b2c_token, setB2c_token] = useState<string | null>(localStorage.getItem("b2c_token"));
    const [userInitials, setUserInitials] = useState<string | null>(null);

    // Decode b2c_token to get the `is_initial_password` flag
    const decodedB2c_token = decodeB2c_token(b2c_token);
     const  role_code = decodedB2c_token?.role_code
    const isInitialPassword = decodedB2c_token?.is_initial_password ?? false;

    // Honour the return path only for a student who is fully set up.
    // A student on an initial password must still go through change-password,
    // and admins/assessors have no business landing on a checkout page.
    const returnTo =
      role_code === "STUDENT" && !isInitialPassword
        ? getSafeReturnTo((location.state as { from?: string } | null)?.from)
        : null;

    const apiUrl = process.env.REACT_APP_API_URL || 'https://default-url.com';
  
    useEffect(() => {
      if (b2c_token) {
        try {
          const decoded: { sub: string, role_code:string } = jwtDecode(b2c_token); // Decode JWT
          const username = decoded.sub || ""; // Default to an empty string if sub is undefined
  
          // Extract initials (first and last character or first two letters)
          const initials =
          username.length > 1
            ? (username[0] + username[username.length - 1]).toUpperCase()
            : username[0]?.toUpperCase() || "";
  
          setUserInitials(initials || decoded.sub); // Use initials or full name

        } catch (err) {
          console.error("Failed to decode b2c_token:", err);
          setUserInitials(null);
        }
      } else {
        setUserInitials(null);
      }
    }, [b2c_token]);

    useEffect(() => {
  const handleStorageChange = (e: StorageEvent) => {
    if (e.key === "b2c_token") {
      const newB2c_token = localStorage.getItem("b2c_token");
      setB2c_token(newB2c_token);
    }
  };

  window.addEventListener("storage", handleStorageChange);
  return () => window.removeEventListener("storage", handleStorageChange);
}, []);

useEffect(() => {
  const handleTokenUpdated = () => {
    const current = localStorage.getItem("b2c_token");
    setB2c_token(current);
  };

  window.addEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated);
  return () => window.removeEventListener(TOKEN_UPDATED_EVENT, handleTokenUpdated);
}, []);

  
/*     const handleLogin = (newB2c_token: string) => {
      localStorage.setItem("b2c_token", newB2c_token);
      setB2c_token(newB2c_token);
    }; */
  
    const handleLogin = (newB2c_token: string, newRefreshToken: string) => {
  localStorage.setItem("b2c_token", newB2c_token);
  localStorage.setItem("b2c_refresh_token", newRefreshToken);
  setB2c_token(newB2c_token);
};

const handleLogout = async () => {
  const refreshToken = localStorage.getItem("b2c_refresh_token");

  if (refreshToken) {
    try {
      await axios.post(`${apiUrl}/b2c/logout`, { refresh_token: refreshToken });
    } catch (err) {
      console.error("Failed to revoke refresh token:", err);
      // proceed with local logout regardless -- don't block the user
    }
  }

  localStorage.removeItem("b2c_token");
  localStorage.removeItem("b2c_refresh_token");
  setB2c_token(null);
  setUserInitials(null);

  navigate(`/practice/login/`, { replace: true });
};
/*     const handleLogout = () => {
      localStorage.removeItem("b2c_token");
      setB2c_token(null);
      setUserInitials(null);
     
      // After successful login, redirect to dashboard
      navigate(`/practice/login/`, { replace: true });

    }; */
  
    const isAuthenticated = !!b2c_token; 
  
    useB2CTokenExpiration(b2c_token);

    return (
       
        <Box >
        <StudentContextProvider accountKey={decodedB2c_token?.sub ?? null}>
        <NavBarB2C
        isAuthenticated={isAuthenticated}
        onLogout={handleLogout}
        userInitials={userInitials}
        />
    
           
          {/* Main content area with flexible width */}
          <Box overflowY="auto">
            <Routes>

              <Route path="/" element={<B2CLandingPage />} />


                {/* Public Routes */}

                <Route path="/free-resources" element={<B2CFreeResources />} />

                <Route path="/payment-success" element={<B2CPaymentSuccess />} />

                 <Route path="/contact" element={<B2CContactUsPage />} />
                 <Route path="/terms" element={<B2CTermsAndConditions />} />
                 <Route path="/privacy" element={<B2CPrivacyPolicy />} />
                 <Route path="/refund-policy" element={<B2CRefundPolicy />} />
                 <Route path="/cookie-policy" element={<B2CCookiePolicy />} />
                 <Route path="/acceptable-use" element={<B2CAcceptableUsePolicy />} />

                 <Route path="/blog" element={<B2CBlogsPage />} />
                <Route path="/blog/:slug" element={<B2CBlogDetailsPage />} />

                <Route
                path=":domainSlug"
                element={<B2CDomainPage />} />

                  {/* Offer Page */}
                <Route path=":domainSlug/:offerSlug" element={<B2COfferPage />} />

                  {/* Checkout */}

                <Route
                  path=":domainSlug/:offerSlug/checkout"
                  element={
                    <B2CCheckoutPage onLogin={handleLogin} />
                  }
                />

                <Route
                  path="login/"
                  element={
                    isAuthenticated ? (
                      <Navigate to={returnTo ?? "../app/"} replace />
                    ) : (
                      <B2CLogin onLogin={handleLogin} />
                    )
                  }
                />

                <Route path="/reset-password" element={<B2CResetPassword />} />

                {/* Admin Routes */}
                {role_code === "ADMIN" && (
                    <>
                    
                    <Route
                        path="/app/change-password"
                        element={
                        isAuthenticated ? (
                            <ChangeB2CPassword handleLogout={handleLogout} />
                        ) : (
                            <Navigate to={`/practice/login/`} replace />
                        )
                        }
                    />

                    <Route
                        path="/app/*"
                        element={
                        isAuthenticated ? (
                            isInitialPassword ? (
                            <Navigate to={`/practice/app/change-password`} replace />
                            ) : (
                            <B2CAdminRouter />
                            )
                        ) : (
                            <Navigate to={`/practice/login/`} replace />
                        )
                        }
                    />
                    </>
                )}

                {/* Assessor Routes */}
                {role_code === "ASSESSOR" && (
                    <>
                    <Route
                        path="/app/change-password"
                        element={
                        isAuthenticated ? (
                            <ChangeB2CPassword handleLogout={handleLogout} />
                        ) : (
                            <Navigate to={`/practice/login/`} replace />
                        )
                        }
                    />
                    <Route
                        path="/app/*"
                        element={
                        isAuthenticated ? (
                            isInitialPassword ? (
                            <Navigate to={`/practice/app/change-password`} replace />
                            ) : (
                            <B2CAssessorRouter />
                            )
                        ) : (
                        <Navigate to={`/practice/login/`} replace />
                        )
                        }
                    />
                    </>
                )}

                {/* Student Routes */}
                {role_code === "STUDENT" && (
                    <>
                    

                    <Route
                        path="/app/change-password"
                        element={
                        isAuthenticated ? (
                        <ChangeB2CPassword handleLogout={handleLogout} />
                        ) : (
                            <Navigate to={`/practice/login/`} replace />
                        )
                        }
                    />

                    <Route
                        path="/app/*"
                        element={
                        isAuthenticated ? (
                            isInitialPassword ? (
                            <Navigate to={`/practice/app/change-password`} replace />
                            ) : (
                              
                            
                                  <B2CStudentRouter />
                              
                            
                            )
                        ) : (
                            <Navigate to={`/practice/login/`} replace />
                        )
                        }
                    />
                    </>
                )}
            </Routes>
          </Box>
          </StudentContextProvider>
      </Box>

      
      
    );
  };

export default B2CRouter;
